import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Zap, Shield, Clock, Globe, Activity, Star, ArrowRight, User, Menu, X } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSelector from "@/components/LanguageSelector";
import PageTransition from "@/components/PageTransition";
import LogoMarquee from "@/components/LogoMarquee";
import QuantumBackground from "@/components/QuantumBackground";
import SiteFooter from "@/components/SiteFooter";
import SavingsCalculator from "@/components/SavingsCalculator";
import SocialProof from "@/components/SocialProof";
import SandboxTryIt from "@/components/SandboxTryIt";
import { PRICING_LIST } from "@/config/pricing";
import {
  staggerContainer,
  staggerItem,
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  scaleIn,
  hoverScale,
  hoverLift,
  heroText,
  heroBadge,
  heroButton,
  quantumGlow,
} from "@/config/animations";

const LOGO_TAGLINE = "/logo_dark.png";
const LOGO_STACKED = "/logo_dark.png";
const LOGO_LIGHT = "/logo_light.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_qnneal-optimize/artifacts/v3gherem_qnneal-optimization-bgQnneal.png";

const HERO_WORDS = [
  { text: "Agentic AI", gradient: "linear-gradient(90deg, #00F0FF, #00D4AA)" },
  { text: "Quantum", gradient: "linear-gradient(90deg, #00F0FF, #A855F7)" },
];

function RotatingHeroWord() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % HERO_WORDS.length);
    }, 3700);
    return () => clearInterval(interval);
  }, []);
  return (
    <span
      className="inline-block relative overflow-hidden align-bottom text-center"
      style={{ height: "1.15em", minWidth: "4.5em" }}
      data-testid="rotating-hero-word"
    >
      <span className="invisible select-none" aria-hidden="true">Agentic AI</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={HERO_WORDS[index].text}
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -8, opacity: 0, transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] } }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0 flex items-center justify-center"
          style={{
            background: HERO_WORDS[index].gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 0 30px rgba(0, 240, 255, 0.25)",
          }}
        >
          {HERO_WORDS[index].text}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const PLATFORMS = [
  { name: "Claude", color: "#E87B35" },
  { name: "VS Code", color: "#007ACC" },
  { name: "Cursor", color: "#00D4AA" },
  { name: "CrewAI", color: "#8B5CF6" },
  { name: "AutoGen", color: "#10B981" },
  { name: "LangGraph", color: "#F59E0B" },
  { name: "OpenAI", color: "#00A67E" },
  { name: "Manus", color: "#EC4899" },
  { name: "Windsurf", color: "#06B6D4" }
];

const CONSUMER_BRANDS = [
  { name: "Shopify", color: "#7AB55C" },
  { name: "Notion", color: "#FFFFFF" },
  { name: "Figma", color: "#A259FF" },
  { name: "Stripe", color: "#635BFF" },
  { name: "Vercel", color: "#CCCCCC" },
  { name: "Linear", color: "#5E6AD2" },
];

const FEATURES = [
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Quantum Annealing Engine",
    description: "Formulates your workflow as a Constrained Quadratic Model and solves it with simulated quantum annealing."
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Sub-100ms Optimization",
    description: "Full workflow optimization in under 100 milliseconds. Your agents are re-routed before a human blinks."
  },
  {
    icon: <Activity className="w-6 h-6" />,
    title: "58% Token Savings",
    description: "Eliminates redundant agent calls, parallelizes independent tasks, and routes to the most cost-effective capable model."
  },
  {
    icon: <Globe className="w-6 h-6" />,
    title: "MCP Native Integration",
    description: `One endpoint. Connect QNNEAL to any MCP-compatible agent framework in a single config line.`
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Zero Trust Security",
    description: "OAuth 2.0 with PKCE, mTLS between services, AES-256 encryption at rest, enterprise compliance ready."
  },
  {
    icon: <Star className="w-6 h-6" />,
    title: "100K+ Concurrent Users",
    description: "Horizontally scalable microservices on Kubernetes. Built for enterprise-grade traffic from day one."
  }
];

const PRICING = PRICING_LIST;

const TESTIMONIALS = [
  {
    quote: "We switched our 40-agent pipeline from direct OpenAI calls to QNNEAL and cut our monthly spend from $12K to $4.2K. The quantum routing finds model combinations we never would have tested manually.",
    name: "James Whitfield",
    title: "Head of AI Infrastructure, Meridian Financial",
    initials: "JW"
  },
  {
    quote: "The sub-100ms optimization latency was the deciding factor. Our clinical NLP agents can't tolerate routing delays, and QNNEAL delivers faster than our previous manual model selection.",
    name: "Dr. Priya Ramanathan",
    title: "VP of Engineering, Cascade Health Systems",
    initials: "PR"
  },
  {
    quote: "We went from 3 providers to 12 overnight. QNNEAL's gateway handles the auth, format translation, and failover for all of them through a single API key. Our team stopped writing provider adapters and started shipping product.",
    name: "Alex Drummond",
    title: "CTO, Vanguard Robotics",
    initials: "AD"
  }
];

const STEPS = [
  {
    step: "01",
    title: "Connect Your AI Platforms",
    description: "Add one line to your MCP config or install our SDK. QNNEAL discovers your agent team and workflow structure automatically.",
    code: `"mcpServers": {\n  "qnneal": {\n    "url": "https://api.qnneal.ai/mcp"\n  }\n}`
  },
  {
    step: "02",
    title: "Autonomous Optimization",
    description: "Our agentic optimizer autonomously analyzes your workflow costs, scores task complexity, and routes each task to the most cost-effective model across all your providers.",
    code: `→ Parse Workflow DAG      0.2s\n→ Score Task Complexity   0.1s\n→ Multi-Provider Routing  0.3s\n→ Re-route Agents         0.1s\n✓ Optimized in 0.7s`
  },
  {
    step: "03",
    title: "Save Money Automatically",
    description: "Our AI agent continuously optimizes routing as prices and performance change across providers. Token costs drop. Execution time shrinks.",
    code: `Token Reduction:  58%\nSpeed Improvement: 2.1x\nCost Saved:       $12.40\nAgents Re-routed: 12/12`
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [providerStats, setProviderStats] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/providers/stats`).then(res => setProviderStats(res.data)).catch(() => {});
  }, []);

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)] overflow-x-hidden">
        {/* Navigation */}
        <motion.nav 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="fixed top-0 left-0 right-0 z-50 glass border-b border-[var(--qn-border)]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <motion.img 
                src={LOGO_ICON} 
                alt="QNNEAL" 
                className="h-10 w-10 logo-spin"
                whileHover={{ scale: 1.05 }}
              />
              <span className="font-outfit font-bold text-2xl tracking-tight">QNNEAL</span>
            </div>
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a href="#features" className="whitespace-nowrap text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors text-base">{t("landing.nav.features")}</a>
              <a href="#how-it-works" className="whitespace-nowrap text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors text-base">{t("landing.nav.howItWorks")}</a>
              <a href="#try-it" className="whitespace-nowrap text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors text-base">Try It</a>
              <a href="#pricing" className="whitespace-nowrap text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors text-base">{t("landing.nav.pricing")}</a>
              <a href="/system-status" className="flex items-center gap-1.5 whitespace-nowrap text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors text-base" data-testid="nav-status-link">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Status
              </a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <LanguageSelector compact />
              <ThemeToggle />
              <Button 
                variant="ghost" 
                className="hidden lg:inline-flex text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
                data-automation-id="landing-login-btn"
              >
                Log In
              </Button>
              <motion.div {...hoverScale} className="hidden lg:block">
                <Button 
                  className="btn-primary rounded-full px-6"
                  onClick={() => navigate("/login")}
                  data-testid="nav-get-started-btn"
                  data-automation-id="landing-get-started-btn"
                >
                  {t("landing.nav.getStarted")}
                </Button>
              </motion.div>
              <Button 
                variant="ghost" 
                size="icon"
                className="lg:hidden text-[var(--qn-foreground-muted)]"
                onClick={() => setMobileMenuOpen((o) => !o)}
                data-testid="nav-mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile / tablet menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-[var(--qn-border)] glass"
              data-testid="nav-mobile-menu"
            >
              <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
                {[
                  { href: "#features", label: t("landing.nav.features") },
                  { href: "#how-it-works", label: t("landing.nav.howItWorks") },
                  { href: "#try-it", label: "Try It" },
                  { href: "#pricing", label: t("landing.nav.pricing") },
                ].map((it) => (
                  <a key={it.href} href={it.href} onClick={() => setMobileMenuOpen(false)}
                    className="py-2.5 text-base text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-colors">
                    {it.label}
                  </a>
                ))}
                <a href="/system-status" onClick={() => setMobileMenuOpen(false)}
                  className="py-2.5 flex items-center gap-1.5 text-base text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Status
                </a>
                <div className="flex items-center gap-3 pt-3 border-t border-[var(--qn-border)] mt-2">
                  <Button variant="ghost" className="flex-1 text-[var(--qn-foreground-muted)]"
                    onClick={() => navigate("/login")} data-testid="nav-mobile-login-btn">
                    Log In
                  </Button>
                  <Button className="btn-primary rounded-full flex-1"
                    onClick={() => navigate("/login")} data-testid="nav-mobile-get-started-btn">
                    {t("landing.nav.getStarted")}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.nav>

        {/* Hero Section */}
        <section className={`relative pt-32 sm:pt-36 lg:pt-40 pb-24 lg:pb-32 overflow-hidden ${isDark ? 'bg-[#0A0A1A]' : 'bg-[#F8F9FC]'}`}>
          <div className="absolute inset-0">
            <QuantumBackground intensity={0.8} />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: isDark
                  ? "radial-gradient(ellipse 70% 70% at 50% 45%, transparent 0%, rgba(5,5,15,0.3) 60%, rgba(5,5,15,0.7) 100%)"
                  : "radial-gradient(ellipse 70% 70% at 50% 45%, transparent 0%, rgba(248,249,252,0.0) 60%, rgba(248,249,252,0.5) 100%)",
                zIndex: 1,
              }}
            />
          </div>
          
          <div className="relative z-10 max-w-screen-xl 2xl:max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <motion.div {...heroBadge}>
              <Badge className="mb-6 sm:mb-8 bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/20 px-4 sm:px-6 py-1.5 sm:py-2 text-sm sm:text-base hover:bg-[var(--qn-primary)] hover:text-[#0a0a1a] hover:border-[var(--qn-primary)] transition-colors cursor-default" data-testid="hero-pill-badge">
                {t("landing.hero.badge")}
              </Badge>
            </motion.div>
            
            <motion.h1 
              {...heroText}
              className={`font-outfit font-bold tracking-tighter mb-6 sm:mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}
              style={{ fontSize: "clamp(2.5rem, 5vw, 5.5rem)", lineHeight: 1.05 }}
            >
              {t("landing.hero.the")} <RotatingHeroWord /> {t("landing.hero.platformThat")}{" "}
              <span className="text-gradient">{t("landing.hero.optimizesYour")} {t("landing.hero.aiWorkflows")}</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className={`max-w-3xl 2xl:max-w-4xl mx-auto mb-10 sm:mb-12 leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-500'}`}
              style={{ fontSize: "clamp(1rem, 1.5vw, 1.35rem)" }}
            >
              {t("landing.hero.subtitle")}
            </motion.p>

            <motion.div 
              {...heroButton}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-16 sm:mb-20"
            >
              <motion.div {...hoverScale} {...quantumGlow} className="rounded-full">
                <Button 
                  className="btn-primary rounded-full"
                  style={{ fontSize: "clamp(1rem, 1.2vw, 1.25rem)", padding: "1.25rem 2.5rem" }}
                  onClick={() => navigate("/login")}
                  data-testid="hero-start-btn"
                  data-automation-id="hero-start-free-btn"
                >
                  {t("landing.hero.startFree")}
                  <ArrowRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
                </Button>
              </motion.div>
              <motion.div {...hoverScale}>
                <Button 
                  variant="outline"
                  className="btn-secondary rounded-full"
                  style={{ fontSize: "clamp(1rem, 1.2vw, 1.25rem)", padding: "1.25rem 2.5rem" }}
                  onClick={() => navigate("/login?demo=true")}
                  data-testid="hero-demo-btn"
                  data-automation-id="hero-view-demo-btn"
                >
                  {t("landing.hero.viewDemo")}
                </Button>
              </motion.div>
            </motion.div>

            {/* Stats — Live provider data + core metrics */}
            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-5xl 2xl:max-w-6xl mx-auto mb-16"
            >
              {[
                { display: "58%", label: t("landing.stats.tokenReduction") },
                { display: providerStats ? `${providerStats.total_providers}+` : "30+", label: "Providers" },
                { display: providerStats ? `${providerStats.total_models}+` : "200+", label: "Models" },
                { display: "<100ms", label: t("landing.stats.optimizationLatency") }
              ].map((stat, i) => (
                <motion.div 
                  key={i} 
                  variants={staggerItem}
                  whileHover={{ y: -4, boxShadow: "var(--qn-shadow-md)" }}
                  className={`rounded-xl p-5 sm:p-6 lg:p-8 border backdrop-blur-sm ${isDark ? 'bg-gray-900/60 border-white/10' : 'bg-white/70 border-gray-200/50'}`}
                  data-testid={`landing-stat-${i}`}
                >
                  <div className="metric-value text-[var(--qn-primary)] mb-2" style={{ fontSize: "clamp(1.75rem, 3vw, 3rem)" }}>
                    {stat.display}
                  </div>
                  <div className={`${isDark ? 'text-white/60' : 'text-gray-500'}`} style={{ fontSize: "clamp(0.875rem, 1.1vw, 1.125rem)" }}>{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
          
          {/* Bottom fade from hero to content */}
          <div className="absolute bottom-0 left-0 right-0 h-32 z-[5]" style={{ background: "linear-gradient(to top, var(--qn-background), transparent)" }} />
        </section>

        {/* Logo Marquee — Platform Showcase */}
        <LogoMarquee />

        {/* Features Section */}
        <section id="features" className="py-20 sm:py-24 lg:py-32 relative">
          <div className="max-w-screen-xl 2xl:max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16 lg:mb-20"
            >
              <Badge className="mb-4 bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)] border-[var(--qn-secondary)]/20 text-sm sm:text-base">{t("landing.features.badge")}</Badge>
              <h2 className="font-outfit font-bold tracking-tight mb-4" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
                {t("landing.features.title")}
              </h2>
              <p className="text-[var(--qn-foreground-muted)] max-w-2xl 2xl:max-w-3xl mx-auto" style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.15rem)" }}>
                {t("landing.features.subtitle")}
              </p>
            </motion.div>

            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8"
            >
              {[
                { icon: <Zap className="w-6 h-6 sm:w-7 sm:h-7" />, tKey: "f1" },
                { icon: <Clock className="w-6 h-6 sm:w-7 sm:h-7" />, tKey: "f2" },
                { icon: <Activity className="w-6 h-6 sm:w-7 sm:h-7" />, tKey: "f3" },
                { icon: <Globe className="w-6 h-6 sm:w-7 sm:h-7" />, tKey: "f4" },
                { icon: <Shield className="w-6 h-6 sm:w-7 sm:h-7" />, tKey: "f5" },
                { icon: <Star className="w-6 h-6 sm:w-7 sm:h-7" />, tKey: "f6" },
              ].map((feature, i) => (
                <motion.div 
                  key={i} 
                  variants={staggerItem}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px -20px rgba(6, 182, 212, 0.3)" }}
                  className="feature-card rounded-xl p-6 sm:p-8 lg:p-10"
                >
                  <motion.div 
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-[var(--qn-primary-muted)] flex items-center justify-center text-[var(--qn-primary)] mb-4 sm:mb-5"
                  >
                    {feature.icon}
                  </motion.div>
                  <h3 className="font-outfit font-semibold mb-3" style={{ fontSize: "clamp(1.1rem, 1.4vw, 1.35rem)" }}>{t(`landing.features.${feature.tKey}Title`)}</h3>
                  <p className="text-[var(--qn-foreground-muted)] leading-relaxed" style={{ fontSize: "clamp(0.9rem, 1.1vw, 1.05rem)" }}>{t(`landing.features.${feature.tKey}Desc`)}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-20 sm:py-24 lg:py-32 bg-[var(--qn-background-secondary)]">
          <div className="max-w-screen-xl 2xl:max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16 lg:mb-20"
            >
              <Badge className="mb-4 bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/20 text-sm sm:text-base">{t("landing.howItWorks.badge")}</Badge>
              <h2 className="font-outfit font-bold tracking-tight mb-4" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
                {t("landing.howItWorks.title")}
              </h2>
              <p className="text-[var(--qn-foreground-muted)] max-w-2xl 2xl:max-w-3xl mx-auto" style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.15rem)" }}>
                {t("landing.howItWorks.subtitle")}
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-3 gap-8 lg:gap-10">
              {STEPS.map((step, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: i === 0 ? -30 : i === 2 ? 30 : 0, y: i === 1 ? 30 : 0 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2 }}
                  className="relative"
                >
                  <div className="font-outfit font-bold text-[var(--qn-foreground)]/5 mb-4" style={{ fontSize: "clamp(3rem, 5vw, 4.5rem)" }}>{step.step}</div>
                  <h3 className="font-outfit font-semibold mb-3" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.65rem)" }}>{t(`landing.howItWorks.s${i + 1}Title`)}</h3>
                  <p className="text-[var(--qn-foreground-muted)] mb-6 leading-relaxed" style={{ fontSize: "clamp(0.9rem, 1.1vw, 1.05rem)" }}>{t(`landing.howItWorks.s${i + 1}Desc`)}</p>
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="bg-[var(--qn-background)] rounded-lg p-4 sm:p-5 font-mono overflow-x-auto border border-[var(--qn-border)]"
                    style={{ fontSize: "clamp(0.75rem, 0.9vw, 0.9rem)" }}
                  >
                    <pre className="text-[var(--qn-primary)]/80">{step.code}</pre>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Savings Calculator */}
        <SavingsCalculator />
        <SocialProof />

        {/* Framework Integrations */}
        <section className="py-20 sm:py-24 lg:py-32 relative">
          <div className="max-w-screen-xl 2xl:max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16 lg:mb-20"
            >
              <Badge className="mb-4 bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/20 text-sm sm:text-base">Integrations</Badge>
              <h2 className="font-outfit font-bold tracking-tight mb-4" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
                Works With Your Stack
              </h2>
              <p className="text-[var(--qn-foreground-muted)] max-w-2xl 2xl:max-w-3xl mx-auto" style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.15rem)" }}>
                Drop-in integrations for the most popular AI frameworks. One API key, every provider, zero vendor lock-in.
              </p>
            </motion.div>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8"
            >
              {[
                { name: "LangChain", desc: "Custom chat model for chains, agents, and RAG pipelines", code: "from qnneal import QNNEALChatModel\nllm = QNNEALChatModel(model=\"gpt-4o\")", color: "#1C3C3C" },
                { name: "LlamaIndex", desc: "Custom LLM for query engines, indices, and data connectors", code: "from qnneal import QNNEALLLM\nllm = QNNEALLLM(model=\"gpt-4o\")", color: "#7C3AED" },
                { name: "Vercel AI SDK", desc: "Edge-compatible provider for useChat and useCompletion hooks", code: "const qnneal = createOpenAICompatible({\n  baseURL: 'https://api.qnneal.com/v1'\n})", color: "#000000" },
                { name: "CrewAI", desc: "LLM wrapper for multi-agent task orchestration", code: "from qnneal import QNNEALLLM\nagent = Agent(llm=QNNEALLLM())", color: "#8B5CF6" },
                { name: "AutoGen", desc: "Model client for Microsoft AutoGen conversations", code: "assistant.register_model_client(\n  model_client_cls=QNNEALModelClient\n)", color: "#10B981" },
                { name: "Semantic Kernel", desc: "AI connector for Microsoft SK plugins and planners", code: "kernel.add_service(\n  QNNEALChatCompletion(service_id=\"qnneal\")\n)", color: "#0078D4" },
              ].map((fw, i) => (
                <motion.div
                  key={fw.name}
                  variants={staggerItem}
                  whileHover={{ y: -4, boxShadow: "0 12px 30px -10px rgba(6, 182, 212, 0.15)" }}
                  className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 sm:p-6 lg:p-8 transition-all"
                  data-testid={`integration-card-${fw.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: fw.color }}>
                      {fw.name[0]}
                    </div>
                    <h3 className="font-outfit font-semibold" style={{ fontSize: "clamp(1rem, 1.3vw, 1.2rem)" }}>{fw.name}</h3>
                  </div>
                  <p className="text-[var(--qn-foreground-muted)] mb-4" style={{ fontSize: "clamp(0.85rem, 1vw, 0.95rem)" }}>{fw.desc}</p>
                  <div className="bg-[var(--qn-background)] rounded-lg p-3 sm:p-4 font-mono overflow-x-auto border border-[var(--qn-border)]" style={{ fontSize: "clamp(0.7rem, 0.85vw, 0.85rem)" }}>
                    <pre className="whitespace-pre-wrap text-[var(--qn-primary)]/80">{fw.code}</pre>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            <div className="text-center mt-8">
              <p className="text-sm text-[var(--qn-foreground-muted)]">
                {providerStats ? `${providerStats.total_providers}+ providers` : "65+ providers"} &middot; {providerStats ? `${providerStats.total_models}+ models` : "350+ models"} &middot; 6 framework integrations &middot; 10 SDK languages
              </p>
            </div>
          </div>
        </section>

        {/* Try It Now Sandbox */}
        <section className="bg-[var(--qn-background-secondary)]">
          <SandboxTryIt />
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <Badge className="mb-4 bg-[var(--qn-success-muted)] text-[var(--qn-success)] border-[var(--qn-success)]/20">{t("landing.pricing.badge")}</Badge>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight mb-4">
                {t("landing.pricing.title")}
              </h2>
              <p className="text-[var(--qn-foreground-muted)] max-w-2xl mx-auto">
                {t("landing.pricing.subtitle")}
              </p>
            </motion.div>

            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto"
            >
              {PRICING.map((plan, i) => {
                const planKey = plan.name.toLowerCase();
                const tp = t(`plans.${planKey}`, { returnObjects: true });
                return (
                <motion.div 
                  key={i}
                  variants={staggerItem}
                  whileHover={{ scale: 1.03 }}
                  className={`pricing-card rounded-2xl p-8 ${plan.featured ? 'featured' : ''}`}
                >
                  {plan.featured && (
                    <Badge className="mb-4 bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/20">{t("landing.pricing.mostPopular")}</Badge>
                  )}
                  <h3 className="font-outfit font-semibold text-2xl mb-2">{tp?.name || plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="metric-value text-4xl">{plan.price}</span>
                    <span className="text-[var(--qn-foreground-subtle)]">{tp?.period ?? plan.period}</span>
                  </div>
                  <p className="text-[var(--qn-foreground-muted)] text-base mb-6">{tp?.desc || plan.description}</p>
                  
                  <motion.div {...hoverScale}>
                    <Button 
                      className={`w-full mb-6 ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => navigate("/login")}
                      data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                    >
                      {tp?.cta || plan.cta}
                    </Button>
                  </motion.div>

                  <ul className="space-y-3">
                    {(tp?.features || plan.features).map((feature, j) => (
                      <motion.li 
                        key={j} 
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: j * 0.05 }}
                        className="flex items-start gap-3 text-base text-[var(--qn-foreground-muted)]"
                      >
                        <Check className="w-4 h-4 text-[var(--qn-success)] mt-0.5 flex-shrink-0" />
                        {feature}
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
                );
              })}
            </motion.div>

            <p className="text-center text-sm text-[var(--qn-foreground-subtle)] mt-6" data-testid="pricing-disclaimer">
              {t("plans.disclaimer")}
            </p>
            <div className="text-center mt-4">
              <Button variant="link" className="text-[var(--qn-primary)] hover:text-[var(--qn-primary)]/80" onClick={() => navigate("/pricing")} data-testid="view-all-plans-btn">
                View full plan comparison <ArrowRight className="w-4 h-4 ml-1 inline" />
              </Button>
            </div>
          </div>
        </section>
        <section className="py-24 bg-[var(--qn-background-secondary)]">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <Badge className="mb-4 bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)] border-[var(--qn-secondary)]/20">{t("landing.testimonials.badge")}</Badge>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight">
                {t("landing.testimonials.title")}
              </h2>
            </motion.div>

            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8"
            >
              {TESTIMONIALS.map((testimonial, i) => (
                <motion.div 
                  key={i} 
                  variants={staggerItem}
                  whileHover={{ y: -4 }}
                  className="card-themed rounded-xl p-8"
                >
                  <p className="text-[var(--qn-foreground-muted)] mb-6 italic">"{t(`landing.testimonials.t${i + 1}`)}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--qn-primary)] to-[var(--qn-secondary)] flex items-center justify-center font-semibold text-white">
                      {testimonial.initials}
                    </div>
                    <div>
                      <div className="font-semibold">{testimonial.name}</div>
                      <div className="text-base text-[var(--qn-foreground-subtle)]">{testimonial.title}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative">
          <div 
            className={`absolute inset-0 bg-cover bg-center ${isDark ? 'opacity-20' : 'opacity-10'}`}
            style={{ backgroundImage: `url(${HERO_BG})` }}
          />
          <div className="hero-overlay absolute inset-0" />
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative max-w-4xl mx-auto px-6 text-center"
          >
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight mb-6">
              {t("landing.cta.title")}
            </h2>
            <p className="text-[var(--qn-foreground-muted)] mb-8">
              {t("landing.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.div {...hoverScale} {...quantumGlow} className="rounded-full">
                <Button 
                  className="btn-primary text-lg px-8 py-6 rounded-full"
                  onClick={() => navigate("/login")}
                  data-testid="cta-start-btn"
                >
                  {t("landing.cta.getStarted")}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
              <motion.div {...hoverScale}>
                <Button 
                  variant="outline"
                  className="btn-secondary text-lg px-8 py-6 rounded-full"
                  onClick={() => navigate("/login?demo=true")}
                  data-testid="cta-demo-btn"
                >
                  {t("landing.cta.viewDemo")}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <SiteFooter />
      </div>
    </PageTransition>
  );
}
